import { z } from 'zod';

/**
 * Liman listesi (ADR 0048 §1): paketli NGA World Port Index satırları ve şirketin kendi limanları.
 * Kod UN/LOCODE (boşluksuz) ya da `WPI<numara>`; ülke ISO 3166-1 alfa-2. Ülke adı saklanmaz,
 * arayüzde dile göre yazılır.
 */

/** Liman büyüklüğü (WPI "Harbor Size"): Large, Medium, Small, Very Small. */
export type PortSize = 'L' | 'M' | 'S' | 'V';

export interface PortRecord {
  code: string;
  name: string;
  country: string;
  lat: number;
  lon: number;
  altNames: readonly string[];
  size: PortSize | null;
  /** Paketli liste ya da şirketin limanı (`company_ports`). */
  source: 'wpi' | 'company';
}

/** Liman kodu: UN/LOCODE (5 karakter) ya da WPI numarası (kaynakta ondalıklı numara da var). */
export const PORT_CODE_PATTERN = /^(?:[A-Z]{2}[A-Z0-9]{3}|WPI\d{1,6}(?:\.\d{1,2})?)$/;
/** Şirket limanının kodu: UN/LOCODE biçiminde; ilk iki harf ülke. */
export const COMPANY_PORT_CODE_PATTERN = /^[A-Z]{2}[A-Z0-9]{3}$/;

/**
 * Kullanıcıya gösterilen liman kodu (ADR 0054 §1): UN/LOCODE; WPI numarası iç koddur, gösterilmez
 * (`null`; ekran yalnız ülkeyi yazar). Veride kod aynen saklanır.
 */
export function displayPortCode(code: string | null | undefined): string | null {
  return code && COMPANY_PORT_CODE_PATTERN.test(code) ? code : null;
}

const missing = (input: unknown) => input === undefined || input === null;

/**
 * Formlardaki liman kodu (ADR 0048 §3): isteğe bağlı; büyük harfe çevrilir; boşsa null (liman
 * listede yoksa yalnız serbest metin yazılır).
 */
export const portCodeField = () =>
  z
    .string({ error: (issue) => (missing(issue.input) ? 'REQUIRED' : 'INVALID_PORT_CODE') })
    .transform((value) => value.trim().toUpperCase())
    .pipe(z.union([z.literal(''), z.string().regex(PORT_CODE_PATTERN, 'INVALID_PORT_CODE')]))
    .transform((value) => (value === '' ? null : value))
    .nullish()
    .transform((value) => value ?? null);

const SIZES = new Set(['L', 'M', 'S', 'V']);

/** `@felix/sea-data` `PORTS_TSV` satırları (build-ports.mjs). */
export function parsePortsTsv(tsv: string): PortRecord[] {
  if (tsv.trim() === '') return [];
  return tsv.split('\n').map((line) => {
    const [code = '', name = '', country = '', lat = '', lon = '', alt = '', size = ''] =
      line.split('\t');
    return {
      code,
      name,
      country,
      lat: Number(lat),
      lon: Number(lon),
      altNames: alt
        .split(';')
        .map((value) => value.trim())
        .filter((value) => value !== ''),
      size: SIZES.has(size) ? (size as PortSize) : null,
      source: 'wpi' as const,
    };
  });
}

/** Arama için: küçük harf, aksansız; Türkçe ı ve İ dahil. */
export function normalizeSearch(text: string): string {
  return text
    .replace(/İ/g, 'i')
    .replace(/ı/g, 'i')
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
}

const SIZE_RANK: Record<string, number> = { L: 0, M: 1, S: 2, V: 3 };

export interface PortSearchOptions {
  limit?: number;
  /** Ülke kodundan ülke adı (arayüz dili); ülke adıyla aramayı açar. */
  countryName?: (code: string) => string;
}

/**
 * Ad, diğer ad, kod ve ülkeye göre arama. Sıra: kodla tam eşleşme, ad başı, diğer ad ya da
 * sözcük başı, ülke, içerir; eşitlikte büyük liman önce, sonra ad. Birden çok sözcükte her biri
 * bir alanda geçmelidir.
 */
export function searchPorts(
  ports: readonly PortRecord[],
  query: string,
  options: PortSearchOptions = {},
): PortRecord[] {
  const q = normalizeSearch(query);
  if (q === '') return [];
  const compactCode = query.replace(/\s/g, '').toUpperCase();
  const tokens = q.split(' ');
  const scored: { port: PortRecord; score: number }[] = [];
  for (const port of ports) {
    const name = normalizeSearch(port.name);
    const alts = port.altNames.map(normalizeSearch);
    const country = normalizeSearch(options.countryName?.(port.country) ?? '');
    const code = port.code.toLowerCase();
    const haystack = [name, ...alts, code, port.country.toLowerCase(), country];
    if (!tokens.every((token) => haystack.some((field) => field.includes(token)))) continue;
    let score = 5;
    if (port.code === compactCode) score = 0;
    else if (name.startsWith(q)) score = 1;
    else if (alts.some((alt) => alt.startsWith(q)) || name.split(' ').some((w) => w.startsWith(q)))
      score = 2;
    else if (q === port.country.toLowerCase() || (country !== '' && country.startsWith(q)))
      score = 3;
    else if (name.includes(q) || alts.some((alt) => alt.includes(q))) score = 4;
    scored.push({ port, score });
  }
  scored.sort(
    (a, b) =>
      a.score - b.score ||
      (SIZE_RANK[a.port.size ?? ''] ?? 4) - (SIZE_RANK[b.port.size ?? ''] ?? 4) ||
      a.port.name.localeCompare(b.port.name),
  );
  return scored.slice(0, options.limit ?? 20).map((s) => s.port);
}

/**
 * AB ve AEA ülkeleri (ADR 0048 §1): AB 27 + İzlanda, Lihtenştayn, Norveç; ayrı ISO kodu olan AB en
 * dış bölgeleri (TFEU m. 349: Fransız Guyanası, Guadeloupe, Martinik, Réunion, Mayotte,
 * Saint-Martin). Kanarya, Azor ve Madeira kendi ülkelerinin kodundadır. MRV kapsamı (EU 2015/757)
 * Faz 3'te ayrıca doğrulanır.
 */
export const EU_EEA_COUNTRIES: ReadonlySet<string> = new Set([
  'AT',
  'BE',
  'BG',
  'HR',
  'CY',
  'CZ',
  'DK',
  'EE',
  'FI',
  'FR',
  'DE',
  'GR',
  'HU',
  'IE',
  'IT',
  'LV',
  'LT',
  'LU',
  'MT',
  'NL',
  'PL',
  'PT',
  'RO',
  'SK',
  'SI',
  'ES',
  'SE',
  'IS',
  'LI',
  'NO',
  'GF',
  'GP',
  'MQ',
  'RE',
  'YT',
  'MF',
]);

export function isEuEeaCountry(country: string): boolean {
  return EU_EEA_COUNTRIES.has(country);
}
